import React, { useState, useRef, useEffect } from 'react';
import { motion, PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight, ExternalLink, Heart, X } from 'lucide-react';
import SimplePDFViewer from './SimplePDFViewer';

const MobileProjectSwiper = ({ projects, onOpenFullPdf, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [direction, setDirection] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const constraintsRef = useRef(null);

  // Update current index when initialIndex changes
  useEffect(() => {
    if (initialIndex >= 0 && initialIndex < projects.length) {
      setCurrentIndex(initialIndex);
    }
  }, [initialIndex, projects.length]);

  const handleDragEnd = (event, info) => {
    setIsDragging(false);
    const swipeThreshold = 100;
    
    if (Math.abs(info.offset.x) > swipeThreshold) {
      if (info.offset.x > 0 && currentIndex > 0) {
        // Swipe right - go to previous
        setDirection(-1);
        setCurrentIndex(currentIndex - 1);
      } else if (info.offset.x < 0 && currentIndex < projects.length - 1) {
        // Swipe left - go to next
        setDirection(1);
        setCurrentIndex(currentIndex + 1);
      }
    }
  };

  const goToNext = () => {
    if (currentIndex < projects.length - 1) {
      setDirection(1);
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const currentProject = projects[currentIndex];

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Progress indicator */}
      <div className="flex justify-center items-center gap-2 mb-4 flex-shrink-0">
        {projects.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-primary-600' : 'bg-primary-200'
            }`}
          />
        ))}
      </div>

      {/* Card container */}
      <div className="flex-1 relative overflow-hidden" ref={constraintsRef}>
        <motion.div
          key={currentIndex}
          initial={{ 
            x: direction > 0 ? 400 : -400,
            opacity: 0,
            scale: 0.8
          }}
          animate={{ 
            x: 0,
            opacity: 1,
            scale: 1
          }}
          exit={{ 
            x: direction > 0 ? -400 : 400,
            opacity: 0,
            scale: 0.8
          }}
          transition={{ 
            type: "spring",
            stiffness: 300,
            damping: 30
          }}
          drag="x"
          dragConstraints={constraintsRef}
          dragElastic={0.1}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={handleDragEnd}
          className="w-full h-full"
        >
          <div className="bg-white rounded-2xl shadow-xl border border-primary-100 overflow-y-auto h-full flex flex-col">
            {/* Project content */}
            <div className="flex-1 p-4 overflow-y-auto">
              {/* PDF or Image */}
              {currentProject.pdfPath ? (
                <div className="w-full mb-4">
                  <SimplePDFViewer
                    pdfPath={currentProject.pdfPath}
                    onOpenFullPdf={() => onOpenFullPdf(currentProject.projectId)}
                  />
                </div>
              ) : currentProject.externalLink ? (
                <div className="w-full mb-4">
                  <div className="relative bg-white rounded-lg overflow-hidden shadow-lg">
                    {currentProject.imagePath ? (
                      <div className="w-full flex items-center justify-center p-4 min-h-[200px]">
                        <img 
                          src={currentProject.imagePath} 
                          alt={currentProject.title}
                          className="w-full h-auto object-contain max-w-full"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center w-full min-h-[200px]">
                        <div className="text-primary-600 text-2xl font-bold">
                          {currentProject.title.split(' ').map(word => word[0]).join('')}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => window.open(currentProject.externalLink, '_blank')}
                    className="mt-2 w-full bg-primary-100 text-primary-800 hover:bg-primary-200 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <ExternalLink size={16} />
                    View Report
                  </button>
                </div>
              ) : (
                <div className="w-full mb-4">
                  <div className="aspect-video bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center w-full min-h-[200px]">
                    <div className="text-primary-600 text-2xl font-bold">
                      {currentProject.title.split(' ').map(word => word[0]).join('')}
                    </div>
                  </div>
                </div>
              )}

              {/* Project info */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-primary-900 mb-1">
                    {currentProject.title}
                  </h3>
                  <p className="text-sm text-primary-600 font-medium">
                    {currentProject.company}
                  </p>
                </div>
                
                {/* Category Tag */}
                {currentProject.category && (
                  <div>
                    <span className="inline-block px-2 py-1 bg-primary-800 text-white text-xs font-semibold rounded-full">
                      {currentProject.category}
                    </span>
                  </div>
                )}
                
                <p className="text-sm text-primary-700 leading-relaxed">
                  {currentProject.description}
                </p>
                
                {currentProject.challenge && (
                  <div>
                    <span className="text-xs font-semibold text-primary-900">Challenge:</span>
                    <p className="text-xs text-primary-700">{currentProject.challenge}</p>
                  </div>
                )}
                
                {currentProject.approach && (
                  <div>
                    <span className="text-xs font-semibold text-primary-900">Action:</span>
                    <p className="text-xs text-primary-700">{currentProject.approach}</p>
                  </div>
                )}
                
                {currentProject.outcome && (
                  <div>
                    <span className="text-xs font-semibold text-primary-900">Impact:</span>
                    <p className="text-xs text-primary-700 font-medium">{currentProject.outcome}</p>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-1">
                  {currentProject.technologies && currentProject.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-xs font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="p-4 border-t border-primary-100 flex justify-between items-center">
              <button
                onClick={goToPrevious}
                disabled={currentIndex === 0}
                className="p-2 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={20} />
              </button>
              
              <span className="text-sm text-primary-600 font-medium">
                {currentIndex + 1} of {projects.length}
              </span>
              
              <button
                onClick={goToNext}
                disabled={currentIndex === projects.length - 1}
                className="p-2 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MobileProjectSwiper; 